/**
 * Customer merge dialog — field-by-field "Keep primary / Use secondary" picker.
 *
 * Props:
 *   open, onOpenChange   — controlled state
 *   primary              — full customer object (the survivor)
 *   secondary            — full customer object (will be soft-deleted)
 *   onMerged(result)     — callback after successful merge
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GitMerge, ArrowRight, AlertTriangle, ChevronsRight } from "lucide-react";
import { toast } from "sonner";

const MERGEABLE_FIELDS = [
  { key: "name",            label: "Name" },
  { key: "language",        label: "Language" },
  { key: "phone",           label: "Primary phone", mono: true },
  { key: "country_code",    label: "Country code",  mono: true },
  { key: "alt_phone",       label: "Alt phone",     mono: true },
  { key: "email",           label: "Email" },
  { key: "online_order_no", label: "Online order #", mono: true },
  { key: "store_location",  label: "Store location" },
  { key: "notes",           label: "Internal notes", multiline: true },
];

const REASON_LABEL = {
  same_email: { text: "Same email",  tone: "bg-rose-50 text-rose-700 border-rose-200" },
  phone_near: { text: "Phone match", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  name_fuzzy: { text: "Similar name",tone: "bg-sky-50 text-sky-700 border-sky-200" },
};

export default function MergeCustomerDialog({ open, onOpenChange, primary, secondary, onMerged }) {
  const [choices, setChoices] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reset choices whenever the dialog opens with a fresh secondary
  useEffect(() => {
    if (!open) return;
    const initial = {};
    MERGEABLE_FIELDS.forEach((f) => {
      const pv = (primary?.[f.key] || "").toString().trim();
      const sv = (secondary?.[f.key] || "").toString().trim();
      // Default = keep primary, except when primary is empty AND secondary has a value
      initial[f.key] = (!pv && sv) ? "secondary" : "primary";
    });
    setChoices(initial);
  }, [open, primary?.id, secondary?.id]);

  const hasConflicts = useMemo(() => {
    return MERGEABLE_FIELDS.some((f) => {
      const pv = (primary?.[f.key] || "").toString().trim().toLowerCase();
      const sv = (secondary?.[f.key] || "").toString().trim().toLowerCase();
      return pv && sv && pv !== sv;
    });
  }, [primary, secondary]);

  if (!primary || !secondary) return null;

  const setChoice = (k, v) => setChoices((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/customers/merge", {
        primary_id: primary.id,
        secondary_id: secondary.id,
        field_choices: choices,
      });
      toast.success(`Merged · ${r.data.tickets_moved} ticket${r.data.tickets_moved === 1 ? "" : "s"} repointed`);
      onMerged?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setSubmitting(false);
    }
  };

  const reasons = secondary.match_reasons || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="merge-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <GitMerge className="w-4 h-4 text-indigo-600" /> Merge customers
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Pick a winning value for each field. Tickets, CSAT responses and audit history
            attached to <b>{secondary.name || "(no name)"}</b> will be repointed to{" "}
            <b>{primary.name || "(no name)"}</b>. The secondary record is soft-deleted and can be audited.
          </DialogDescription>
        </DialogHeader>

        {/* Match reasons strip */}
        {reasons.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 -mt-1 text-xs">
            <span className="text-slate-500 uppercase tracking-wider text-[10px]">Why we matched:</span>
            {reasons.map((r) => {
              const meta = REASON_LABEL[r] || { text: r, tone: "bg-slate-50 text-slate-700 border-slate-200" };
              return (
                <Badge key={r} variant="outline" className={meta.tone} data-testid={`reason-${r}`}>
                  {meta.text}
                </Badge>
              );
            })}
          </div>
        )}

        {/* Header row */}
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-200 pb-2">
          <div className="col-span-3">Field</div>
          <div className="col-span-4 flex items-center gap-1">
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Primary</Badge>
            <span className="text-slate-400 normal-case font-normal">(survivor)</span>
          </div>
          <div className="col-span-1 text-center">Pick</div>
          <div className="col-span-4 flex items-center gap-1">
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Secondary</Badge>
            <span className="text-slate-400 normal-case font-normal">(merged-in)</span>
          </div>
        </div>

        <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100 -mt-2">
          {MERGEABLE_FIELDS.map((f) => {
            const pv = (primary[f.key] || "").toString();
            const sv = (secondary[f.key] || "").toString();
            const same = pv.trim().toLowerCase() === sv.trim().toLowerCase();
            const pickedPrimary = choices[f.key] !== "secondary";
            return (
              <div key={f.key} className="grid grid-cols-12 gap-2 items-center py-2.5"
                   data-testid={`merge-row-${f.key}`}>
                <div className="col-span-3 text-xs font-medium text-slate-700">{f.label}</div>
                <div
                  className={`col-span-4 text-xs cursor-pointer px-2 py-1.5 rounded border ${
                    pickedPrimary
                      ? "border-emerald-300 bg-emerald-50/40"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  } ${f.mono ? "font-mono" : ""} break-words`}
                  onClick={() => setChoice(f.key, "primary")}
                  data-testid={`pick-primary-${f.key}`}
                >
                  {pv || <span className="text-slate-400 italic">empty</span>}
                </div>
                <div className="col-span-1 flex justify-center">
                  <ChevronsRight className={`w-3.5 h-3.5 ${pickedPrimary ? "text-emerald-500 rotate-180" : "text-amber-500"}`} />
                </div>
                <div
                  className={`col-span-4 text-xs cursor-pointer px-2 py-1.5 rounded border ${
                    !pickedPrimary
                      ? "border-amber-300 bg-amber-50/40"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  } ${f.mono ? "font-mono" : ""} break-words ${same ? "opacity-60" : ""}`}
                  onClick={() => setChoice(f.key, "secondary")}
                  data-testid={`pick-secondary-${f.key}`}
                >
                  {sv || <span className="text-slate-400 italic">empty</span>}
                  {same && sv && <span className="ml-1 text-[10px] text-slate-400">(same)</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Warning */}
        <div className="mt-2 flex items-start gap-2 p-3 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            All tickets currently linked to the secondary will be moved to the primary.
            The secondary customer record is <b>soft-deleted</b> (kept with a <span className="font-mono">merged_into</span> marker for audit).
            This action is logged but not auto-reversible — double-check before confirming.
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="confirm-merge-btn">
            <GitMerge className="w-3.5 h-3.5 mr-1.5" />
            {submitting ? "Merging…" : "Confirm merge"}
            <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </DialogFooter>
        {!hasConflicts && (
          <p className="text-[10px] text-slate-400 text-center mt-1">
            No field conflicts — primary's values will be preserved unless you flip a row.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
